import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Check, AlertCircle, RefreshCw, Trash2, HelpCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import IconButton from './IconButton';

const ImportModal = ({ isOpen, onClose, onImport, fields = [], extraFields = [] }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Mapping, 3: Preview (optional, maybe integrated in 2)
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [extraValues, setExtraValues] = useState({});
  const [draggedToken, setDraggedToken] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when closed
      setStep(1);
      setFile(null);
      setHeaders([]);
      setRawData([]);
      setMapping({});
      setExtraValues({});
      setDraggedToken(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const processFile = (file) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      '.xlsx',
      '.xls'
    ];
    
    // Simple extension check as backup
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (!isExcel && !validTypes.includes(file.type)) {
      toast.error('Formato de arquivo inválido. Por favor envie .xlsx ou .xls');
      return;
    }

    setFile(file);
    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        // Use 'array' type for better compatibility
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!jsonData || jsonData.length === 0) {
          toast.error('A planilha está vazia');
          setIsProcessing(false);
          return;
        }

        // Ensure headers exist and are strings
        const headerRow = jsonData[0];
        if (!Array.isArray(headerRow)) {
            toast.error('Formato de planilha inválido (cabeçalho não encontrado)');
            setIsProcessing(false);
            return;
        }

        // Handle sparse arrays (empty cells) by using Array.from to fill holes with undefined, then map
        const extractedHeaders = Array.from(headerRow).map(h => (h !== null && h !== undefined) ? String(h).trim() : '');
        const dataRows = jsonData.slice(1);

        setHeaders(extractedHeaders);
        setRawData(dataRows);
        setStep(2);
        
        // Auto-map logic
        autoMapFields(extractedHeaders);

      } catch (error) {
        console.error('Erro ao ler arquivo:', error);
        toast.error(`Erro ao processar o arquivo: ${error.message}`);
      } finally {
        setIsProcessing(false);
      }
    };
    // Use readAsArrayBuffer instead of readAsBinaryString
    reader.readAsArrayBuffer(file);
  };

  const autoMapFields = (fileHeaders) => {
    const newMapping = {};
    fields.forEach(field => {
        if (!field || !field.key) return;
        const fieldLabel = field.label || '';
        const fieldKey = field.key || '';

      // Direct match
      const directMatch = fileHeaders.find(h => h && (h.toLowerCase() === fieldLabel.toLowerCase() || h.toLowerCase() === fieldKey.toLowerCase()));
      if (directMatch) {
        newMapping[field.key] = directMatch;
      } else {
        // Fuzzy match / keywords
        // This is a simple implementation, could be improved
        const keywords = [fieldLabel, fieldKey, fieldLabel.split(' ')[0]].filter(k => k && k.length > 2); // Only valid keywords
        for (const kw of keywords) {
            const match = fileHeaders.find(h => h && h.toLowerCase().includes(kw.toLowerCase()));
            if (match) {
                newMapping[field.key] = match;
                break;
            }
        }
      }
    });
    setMapping(newMapping);
  };

  const handleDragStart = (e, header) => {
    setDraggedToken(header);
    e.dataTransfer.setData('text/plain', header);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e, fieldKey) => {
    e.preventDefault();
    const header = e.dataTransfer.getData('text/plain');
    if (header) {
      setMapping(prev => ({ ...prev, [fieldKey]: header }));
    }
  };

  const handleRemoveMapping = (fieldKey) => {
    setMapping(prev => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const getPreviewData = () => {
    return rawData.slice(0, 5).map((row, index) => {
      const obj = {};
      fields.forEach(field => {
        const header = mapping[field.key];
        if (header) {
          const headerIndex = headers.indexOf(header);
          if (headerIndex !== -1) {
            let val = row[headerIndex];
            if (typeof val === 'string') val = val.trim();
            obj[field.key] = typeof field.transform === 'function' ? field.transform(val) : val;
          }
        }
      });
      return obj;
    });
  };

  const handleImport = () => {
    // Validate required fields
    const missingRequired = fields.filter(f => f.required && !mapping[f.key]);
    if (missingRequired.length > 0) {
      toast.error(`Campos obrigatórios faltando: ${missingRequired.map(f => f.label).join(', ')}`);
      return;
    }

    // Convert rawData to mapped objects
    const mappedData = rawData.map(row => {
      const obj = {};
      fields.forEach(field => {
        const header = mapping[field.key];
        if (header) {
          const headerIndex = headers.indexOf(header);
          if (headerIndex !== -1) {
             let val = row[headerIndex];
             // Basic trimming if string
             if (typeof val === 'string') val = val.trim();
             if (typeof field.transform === 'function') val = field.transform(val);
             obj[field.key] = val;
          }
        }
      });
      return obj;
    });

    onImport(mappedData, extraValues);
  };

  const getMappedHeaderCount = () => Object.keys(mapping).length;
  const isReadyToImport =
    fields.every(f => !f.required || mapping[f.key]) &&
    extraFields.every(ef => !ef.required || extraValues[ef.key]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Upload className="h-6 w-6 text-blue-600" />
            Importar Dados
          </h3>
          <IconButton icon={X} onClick={onClose} variant="secondary" title="Fechar" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                 onClick={() => fileInputRef.current?.click()}
                 onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                 onDrop={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   const files = e.dataTransfer.files;
                   if (files && files.length > 0) {
                     processFile(files[0]);
                   }
                 }}
            >
              <input
                type="file"
                className="hidden"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                ref={fileInputRef}
              />
              <FileSpreadsheet className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900">Clique para selecionar ou arraste sua planilha aqui</p>
              <p className="text-sm text-gray-500 mt-2">Suporta arquivos .xlsx e .xls</p>
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col h-full gap-6">
              {/* Campos adicionais (não vêm da planilha) */}
              {extraFields.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Informações da importação</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {extraFields.map((ef) => (
                      <div key={ef.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {ef.label}{ef.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {ef.type === 'date' ? (
                          <input
                            type="date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            value={extraValues[ef.key] || ''}
                            onChange={(e) => setExtraValues(prev => ({ ...prev, [ef.key]: e.target.value }))}
                          />
                        ) : (
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            value={extraValues[ef.key] || ''}
                            onChange={(e) => setExtraValues(prev => ({ ...prev, [ef.key]: e.target.value }))}
                          >
                            <option value="">Selecione...</option>
                            {(ef.options || []).map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        )}
                        {ef.required && !extraValues[ef.key] && (
                          <p className="mt-1 text-xs text-orange-600">Obrigatório</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Mapeamento de Colunas</p>
                  <p>Arraste os títulos das colunas da sua planilha (à esquerda) para os campos correspondentes do sistema (à direita). Campos com * são obrigatórios.</p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 h-full">
                {/* Source Tokens */}
                <div className="w-full md:w-1/3 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-700">Colunas do Arquivo</h4>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{headers.length} encontradas</span>
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-lg p-4 border border-gray-200 overflow-y-auto max-h-[500px]">
                    <div className="flex flex-wrap gap-2">
                      {headers.map((header, idx) => {
                        const isMapped = Object.values(mapping).includes(header);
                        return (
                          <div
                            key={idx}
                            draggable
                            onDragStart={(e) => handleDragStart(e, header)}
                            className={`px-3 py-2 rounded-md text-sm cursor-grab active:cursor-grabbing border shadow-sm transition-all select-none
                              ${isMapped 
                                ? 'bg-green-50 border-green-200 text-green-700 opacity-75' 
                                : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:shadow-md'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              {isMapped && <Check className="h-3 w-3" />}
                              {header}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Target Slots */}
                <div className="w-full md:w-2/3 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-700">Campos do Sistema</h4>
                    <div className="flex gap-2">
                        <IconButton
                            icon={RefreshCw}
                            onClick={() => autoMapFields(headers)}
                            variant="primary"
                            size="sm"
                            title="Remapear Auto"
                        />
                        <IconButton
                            icon={Trash2}
                            onClick={() => setMapping({})}
                            variant="danger"
                            size="sm"
                            title="Limpar"
                        />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 overflow-y-auto max-h-[500px] pr-2">
                    {fields.map((field) => (
                      <div 
                        key={field.key}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, field.key)}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 border-dashed transition-all
                          ${mapping[field.key] 
                            ? 'border-green-300 bg-green-50' 
                            : field.required 
                                ? 'border-orange-200 bg-orange-50/50' 
                                : 'border-gray-200 bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center gap-2 min-w-[150px]">
                            <span className={`text-sm font-medium ${field.required ? 'text-gray-900' : 'text-gray-600'}`}>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </span>
                            {field.required && !mapping[field.key] && (
                                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">Obrigatório</span>
                            )}
                        </div>
                        
                        <div className="flex-1 mx-4">
                            {mapping[field.key] ? (
                                <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-green-200 shadow-sm">
                                    <span className="text-sm text-gray-800 font-medium truncate">{mapping[field.key]}</span>
                                    <IconButton
                                        icon={X}
                                        onClick={() => handleRemoveMapping(field.key)}
                                        variant="secondary"
                                        size="sm"
                                        title="Remover mapeamento"
                                        className="ml-2"
                                    />
                                </div>
                            ) : (
                                <div className="text-sm text-gray-400 italic text-center py-2">
                                    Arraste uma coluna aqui
                                </div>
                            )}
                        </div>

                        {/* Accessibility Fallback */}
                        <div className="w-[40px] flex justify-end">
                            <select 
                                className="w-5 h-5 opacity-0 absolute cursor-pointer"
                                onChange={(e) => {
                                    if (e.target.value) setMapping(prev => ({ ...prev, [field.key]: e.target.value }));
                                    else handleRemoveMapping(field.key);
                                }}
                                value={mapping[field.key] || ''}
                                aria-label={`Selecionar coluna para ${field.label}`}
                            >
                                <option value="">Selecione...</option>
                                {headers.map(h => (
                                    <option key={h} value={h}>{h}</option>
                                ))}
                            </select>
                            <ArrowRight className={`h-5 w-5 ${mapping[field.key] ? 'text-green-500' : 'text-gray-300'}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-4 border-t pt-4">
                 <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                    Prévia dos Dados
                    <span className="text-xs font-normal text-gray-500">(Primeiras 5 linhas)</span>
                 </h4>
                 <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {fields.map(f => (
                                    <th key={f.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        {f.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {getPreviewData().map((row, i) => (
                                <tr key={i}>
                                    {fields.map(f => (
                                        <td key={f.key} className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap max-w-[150px] truncate">
                                            {String(row[f.key] || '-')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between items-center rounded-b-lg">
          <IconButton icon={X} onClick={onClose} variant="secondary" title="Cancelar" />
          
          <div className="flex gap-3">
            {step === 2 && (
                 <IconButton
                 icon={ArrowLeft}
                 onClick={() => setStep(1)}
                 variant="secondary"
                 title="Voltar para Upload"
               />
            )}
            {step === 2 && (
                <IconButton
                    icon={Upload}
                    onClick={handleImport}
                    disabled={!isReadyToImport}
                    variant="success"
                    title={`Importar ${rawData.length} Registros`}
                />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
